import React, {useCallback, useContext, useMemo} from "react";
import OwlCarousel from "react-owl-carousel";
import classnames from "classnames";
import LazyImage from "../../../../../../../../components/app-specific/lazy-image";
import {UserHomepageControllerContext, UserHomepageViewContext} from "../../../../../../index";
import {UserHomepageEventEmitter} from "../../../../../../models";
import {ReactComponent as Prev} from "../../../../../../../../../assets/images/web-homepage/slide-show-prev.svg";
import {ReactComponent as Next} from "../../../../../../../../../assets/images/web-homepage/slide-show-next.svg";
import {useBizFileUrlsResolver} from "../../../../../../../../hooks/use-biz-file-urls-resolver";
import useImageCarouselOwl from "../../../../../../../../hooks/use-image-carousel-owl";
import {IMAGE_CAROUSEL_MARGIN} from "../../../../../../../../../core/services/utils/image-carousel-owl";

const UserWebHomepageImageCarousel = ({component, tabId}) => {
    const controller = useContext(UserHomepageControllerContext);
    const {viewportWidth} = useContext(UserHomepageViewContext);
    const bizFileUrls = useBizFileUrlsResolver((t) => t.bizFile);
    const images = useMemo(() => component?.content?.images ?? [], [component]);
    const imageWidth = component?.content?.imageWidth;
    const imageHeight = component?.content?.imageHeight;

    const {
        sliderRef,
        carouselRef,
        containerIdRef,
        isScrollable,
        hasNext,
        hasPrev,
        slideStyle,
        imageStyle,
        owlClassName,
        owlProps,
        changeSlide,
        onInitialized,
        onChanged,
    } = useImageCarouselOwl({
        images,
        imageWidth,
        imageHeight,
        margin: IMAGE_CAROUSEL_MARGIN,
        viewportWidth,
        reactKey: true,
    });

    /**
     * Handles the click event on an image in the carousel. It triggers the clickContent method of the controller with the appropriate parameters, including the tabId, component id, image id, and the event type for viewing.
     * @type {(function(*): void)|*}
     */
    const onImageClicked = useCallback((image) => {
        controller.clickContent(tabId, component.id, image.id, UserHomepageEventEmitter.view);
    }, [controller, tabId, component.id]);

    /**
     * Retrieves the image source URL for a given image object. It uses the bizFileUrls resolver to get the preview URL based on the fileGuid and fileExtension of the image. If the preview URL is not available, it falls back to using a mockPreviewUrl from the image object.
     * @type {Function}
     */
    const getImageSrc = useCallback((image) => bizFileUrls.getFilePreviewUrl(image?.fileGuid, image?.fileExtension) ?? image?.mockPreviewUrl, [bizFileUrls]);

    const owlCarousel = useMemo(() => (
        <OwlCarousel
            {...owlProps}
            ref={carouselRef}
            onInitialized={onInitialized}
            onChanged={onChanged}
            id={containerIdRef.current}
            className={owlClassName}>
            {images.map((image) => (
                <div
                    key={image.id}
                    id={image.webViewElementId}
                    className={classnames(
                        'user-web-homepage-image-carousel__slide',
                        {'clickable': !!image?.tags?.length},
                    )}
                    style={slideStyle}
                    onClick={() => onImageClicked(image)}>
                    <div
                        className={'image'}
                        style={imageStyle}>
                        <LazyImage
                            src={getImageSrc(image)}
                            alt={image?.tags?.[0]?.name ?? ''}
                            width={imageStyle.width}
                            height={imageHeight}
                            className={'user-web-homepage-image-carousel__img'}
                        />
                    </div>
                </div>
            ))}
        </OwlCarousel>
    ), [owlProps, carouselRef, onInitialized, onChanged, containerIdRef, owlClassName, images, slideStyle, imageStyle, getImageSrc, imageHeight, onImageClicked]);

    return (
        <div className={'user-web-homepage-image-carousel'}>
            <div className={'user-web-homepage-block-header component-title'}>
                <p>{component?.title ?? ''}</p>
            </div>
            <div
                ref={sliderRef}
                className={'user-web-homepage-image-carousel__slider'}>
                {isScrollable &&
                    <button
                        disabled={!hasPrev}
                        className={'slider-icon-button prev'}
                        onClick={() => changeSlide(-1)}>
                        <Prev/>
                    </button>
                }
                {owlCarousel}
                {isScrollable &&
                    <button
                        disabled={!hasNext}
                        className={'slider-icon-button next'}
                        onClick={() => changeSlide(1)}>
                        <Next/>
                    </button>
                }
            </div>
        </div>
    );
}

export default UserWebHomepageImageCarousel;
